// @ts-nocheck
// Ported byte-identically from obd3-gui v1
// (src/features/dashboard/components/WidgetGrid.tsx).
// A responsive CSS-grid of sortable, resizable widget tiles. Drag/drop
// via @dnd-kit (sortable strategy). Column-span per tile is driven by
// SIZE_DEFS.colsLg / colsMd. `autoFit` mode is used by the KPI strip so
// tiles reflow into as many columns as fit at min tile-px width.
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils/cn';
import {
  SIZE_DEFS,
  WIDGETS,
  isWidgetAllowed,
  type LayoutItem,
  type WidgetId,
  type WidgetSize,
} from '../config';
import { renderWidget } from '../widgets/registry';
import { WidgetCard } from './WidgetCard';

interface Props {
  layout: LayoutItem[];
  editing: boolean;
  onReorder: (fromId: WidgetId, toId: WidgetId) => void;
  onResize: (id: WidgetId, size: WidgetSize) => void;
  onRemove: (id: WidgetId) => void;
  role: string;
  autoFit?: boolean;
  minTilePx?: number;
  fixedCols?: number;   // exact column count override (e.g. 5) — beats autoFit
}

export function WidgetGrid({
  layout,
  editing,
  onReorder,
  onResize,
  onRemove,
  role,
  autoFit = false,
  minTilePx = 200,
  fixedCols,
}: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  if (layout.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card/50 py-16 text-center">
        <div className="text-sm font-semibold text-foreground">
          Nothing on this tab yet
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {editing
            ? 'Click "Add widget" to bring something in.'
            : 'Click "Customize" then "Add widget" to start.'}
        </p>
      </div>
    );
  }

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    onReorder(active.id as WidgetId, over.id as WidgetId);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={layout.map((l) => l.id)} strategy={rectSortingStrategy}>
        <div
          className={cn(
            'grid gap-3',
            /* Priority: fixedCols > autoFit > default responsive grid.
             * Fixed mode renders exactly N columns on any viewport,
             * ignoring auto-fit width thresholds. */
            !fixedCols && !autoFit ? 'grid-cols-1 md:grid-cols-6 lg:grid-cols-12' : '',
          )}
          style={
            fixedCols
              ? { gridTemplateColumns: `repeat(${fixedCols}, minmax(0, 1fr))` }
              : autoFit
                ? { gridTemplateColumns: `repeat(auto-fit, minmax(${minTilePx}px, 1fr))` }
                : undefined
          }
        >
          {layout.map((it) => (
            <SortableTile
              key={it.id}
              item={it}
              editing={editing}
              onResize={onResize}
              onRemove={onRemove}
              role={role}
              autoFit={autoFit}
              fixedCols={fixedCols}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

interface SortableTileProps {
  item: LayoutItem;
  editing: boolean;
  onResize: (id: WidgetId, size: WidgetSize) => void;
  onRemove: (id: WidgetId) => void;
  role: string;
  autoFit?: boolean;
  fixedCols?: number;
}

function SortableTile({ item, editing, onResize, onRemove, role, autoFit = false, fixedCols }: SortableTileProps) {
  const desc = WIDGETS.find((w) => w.id === item.id);
  const def = SIZE_DEFS[item.size];

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !editing });

  // Saved layouts can still hold a widget the user may no longer see.
  if (!desc || !isWidgetAllowed(item.id)) return null;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    minHeight: def.minHeight,
  };

  const colSpanMd: Record<number, string> = {
    1: 'md:col-span-1', 2: 'md:col-span-2', 3: 'md:col-span-3',
    4: 'md:col-span-4', 5: 'md:col-span-5', 6: 'md:col-span-6',
  };
  const colSpanLg: Record<number, string> = {
    1: 'lg:col-span-1', 2: 'lg:col-span-2', 3: 'lg:col-span-3',
    4: 'lg:col-span-4', 5: 'lg:col-span-5', 6: 'lg:col-span-6',
    7: 'lg:col-span-7', 8: 'lg:col-span-8', 9: 'lg:col-span-9',
    10: 'lg:col-span-10', 11: 'lg:col-span-11', 12: 'lg:col-span-12',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'min-w-0',
        /* When fixedCols is set (KPI strip forcing 5 per row) every
         * tile spans exactly 1 column of that grid — ignore the
         * SIZE_DEFS col-span classes which were tuned for a 12-col
         * grid and would otherwise cram multiple 5-col spans into
         * few-per-row layouts. */
        fixedCols
          ? 'col-span-1'
          : autoFit
            ? 'col-span-1'
            : cn(
                'col-span-1',
                colSpanMd[def.colsMd] ?? 'md:col-span-6',
                colSpanLg[def.colsLg] ?? 'lg:col-span-6',
              ),
        isDragging && 'z-10 opacity-80',
      )}
    >
      <WidgetCard
        title={desc.title}
        icon={desc.icon}
        editing={editing}
        onRemove={() => onRemove(item.id)}
        description={desc.description}
        dragAttributes={attributes}
        dragListeners={listeners}
        size={item.size}
        onSizeChange={(s) => onResize(item.id, s)}
        compact={desc.tab === 'kpi'}
      >
        {renderWidget(item.id, role)}
      </WidgetCard>
    </div>
  );
}
